// Session scope = the gate the guard is working at. Picked after every sign-in (it is bound to the
// JWT it was chosen under, so a new login asks again — or auto-picks when there is only one gate)
// and survives page reloads within that login. The backend never trusts it: every call re-checks
// that the user is assigned to the gate.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { useAuth } from "@/lib/auth";
import type { GateInfo } from "@/lib/gate";

const STORAGE_KEY = "icm_gate_scope_v1";

export interface SessionScope {
  gate: GateInfo;
  /** Gate admin (central permission): sees the Admin section. */
  isAdmin: boolean;
}

interface StoredScope extends SessionScope {
  token: string;
}

interface ScopeState {
  scope: SessionScope | null;
  setScope: (scope: SessionScope) => void;
  clearScope: () => void;
}

const ScopeContext = createContext<ScopeState | null>(null);

function readStored(): StoredScope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredScope) : null;
  } catch {
    return null;
  }
}

export function SessionScopeProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  // In-memory copy; localStorage only carries it across reloads.
  const [stored, setStored] = useState<StoredScope | null>(readStored);

  // Valid only for the login (token) it was chosen under.
  const scope = useMemo<SessionScope | null>(
    () => (stored && token && stored.token === token ? { gate: stored.gate, isAdmin: !!stored.isAdmin } : null),
    [stored, token]
  );

  const setScope = useCallback(
    (next: SessionScope) => {
      if (!token) return;
      const value: StoredScope = { ...next, token };
      setStored(value);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch {
        // storage unavailable — the scope still applies until reload
      }
    },
    [token]
  );

  const clearScope = useCallback(() => {
    setStored(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return <ScopeContext.Provider value={{ scope, setScope, clearScope }}>{children}</ScopeContext.Provider>;
}

export function useSessionScope() {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useSessionScope must be used within SessionScopeProvider");
  return ctx;
}

/** The current gate — only call inside <RequireScope>. */
export function useGate(): SessionScope {
  const { scope } = useSessionScope();
  if (!scope) throw new Error("useGate needs a chosen gate (wrap the route in RequireScope)");
  return scope;
}
